import React, { useState, useEffect, useContext } from "react";
import * as arrow from "apache-arrow";
import { Box, IconButton, Theme, Checkbox, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CompactTableTextField from "../../components/transformed_custom_view/viewer/CompactTableTextField";
import dayjs from "../../utils/dayjs";
import { shortFormatDateString } from "../../utils/date_format";
import {
  buildMUIColumns,
  makeGetRowFromArrowTableWithIndices,
  getSortedRowIndices,
  calculateColumnTotals,
  formatCellValue,
} from "../../utils/mui_table_utils";
import {
  getSupplyCellBackgroundColor,
  getWOSMeasureKey,
} from "../../utils/supply_color_coding";

// Revert button component (appears only when there are edits)
import RefreshIcon from "@mui/icons-material/Refresh";
// SupplyWalkEditableNestedDetails will be imported from container
// Access global ref without causing rerenders
import { SupplyWalkEditsContext } from "../../contexts/supply_walk_edits/provider";

// MUIFlatTableColumn type definition
export interface MUIFlatTableColumn {
  key: string;
  label: string;
  width?: number;
  align?: "left" | "center" | "right";
  sticky?: boolean;
  isExpandColumn?: boolean;
  cellType?: arrow.DataType;
  renderCell?: (row: any, rowIndex?: number) => React.ReactNode;
  sortable?: boolean;
  onSort?: (e: React.MouseEvent<HTMLTableCellElement>) => void;
  sortDirection?: "asc" | "desc" | undefined;
}

// Type for row edit state (no longer global - will be passed as ref from container)
type RowEditStateMap = Map<
  string,
  {
    isEditing: boolean;
    editValues: Record<string, string>;
    committedValues: Record<string, string>;
    originalValues: Record<string, string>;
    listeners: Set<() => void>;
  }
>;

// Row edit state manager
const getRowEditState = (
  rowKey: string,
  editableColumns: string[],
  row: any,
  rowEditStateRef: RowEditStateMap,
  supplyGuard: any
) => {
  if (!rowEditStateRef.has(rowKey)) {
    // Check if we have persistent data for this row via context
    const persistentData = supplyGuard?.getEdit?.(rowKey);

    rowEditStateRef.set(rowKey, {
      isEditing: false,
      editValues: editableColumns.reduce(
        (acc, col) => {
          let value = String(row[col] || "");

          // Format date columns to mm/dd/yy format
          if (col === "order_placed_date" || col === "order_delivery_date") {
            if (value && value !== "null" && value !== "undefined") {
              try {
                let date;
                if (typeof value === "string" && value.includes("/")) {
                  // Already in MM/DD/YY format
                  date = dayjs(value, "MM/DD/YY");
                } else {
                  // ISO format - parse as UTC
                  date = dayjs.utc(value);
                }
                if (date.isValid()) {
                  value = date.format("MM/DD/YY");
                }
              } catch (e) {
                // Keep original value if parsing fails
              }
            }
          }

          acc[col] = value;
          return acc;
        },
        {} as Record<string, string>
      ),
      committedValues:
        persistentData?.committedValues ||
        editableColumns.reduce(
          (acc, col) => {
            let value = String(row[col] ?? "");
            // Ensure date columns are stored in UTC datetime format
            if (col === "order_placed_date" || col === "order_delivery_date") {
              try {
                let date;
                if (typeof value === "string" && value.includes("/")) {
                  // Already in MM/DD/YY format - convert to UTC
                  date = dayjs(value, "MM/DD/YY").utc();
                } else {
                  // ISO format - parse as UTC
                  date = dayjs.utc(value);
                }
                if (date.isValid()) {
                  value = date.toISOString();
                }
              } catch (e) {
                // Keep original value if parsing fails
              }
            }
            acc[col] = value;
            return acc;
          },
          {} as Record<string, string>
        ),
      originalValues:
        persistentData?.originalValues ||
        editableColumns.reduce(
          (acc, col) => {
            let value = String(row[col] ?? "");
            // Ensure date columns are stored in UTC datetime format
            if (col === "order_placed_date" || col === "order_delivery_date") {
              try {
                let date;
                if (typeof value === "string" && value.includes("/")) {
                  // Already in MM/DD/YY format - convert to UTC
                  date = dayjs(value, "MM/DD/YY").utc();
                } else {
                  // ISO format - parse as UTC
                  date = dayjs.utc(value);
                }
                if (date.isValid()) {
                  value = date.toISOString();
                }
              } catch (e) {
                // Keep original value if parsing fails
              }
            }
            acc[col] = value;
            return acc;
          },
          {} as Record<string, string>
        ),
      listeners: new Set(),
    });
  }
  return rowEditStateRef.get(rowKey)!;
};

// Notify all listeners for a row
const notifyRowListeners = (
  rowKey: string,
  rowEditStateRef: RowEditStateMap
) => {
  const state = rowEditStateRef.get(rowKey);
  if (state) {
    state.listeners.forEach((listener) => listener());
  }
};

// LEVEL 3 CACHE REMOVED FOR TESTING
// Global edit version to invalidate memoized matrix when edits are committed
// let editsVersion = 0;

// Row edit state hook
const useRowEditState = (
  rowKey: string,
  editableColumns: string[],
  row: any,
  rowEditStateRef: RowEditStateMap
) => {
  const supplyGuard = useContext(SupplyWalkEditsContext);

  const [state, setState] = useState(() =>
    getRowEditState(rowKey, editableColumns, row, rowEditStateRef, supplyGuard)
  );

  useEffect(() => {
    const currentState = getRowEditState(
      rowKey,
      editableColumns,
      row,
      rowEditStateRef,
      supplyGuard
    );
    const listener = () => {
      setState({ ...currentState });
    };
    currentState.listeners.add(listener);

    return () => {
      currentState.listeners.delete(listener);
    };
  }, [rowKey, editableColumns, row, rowEditStateRef, supplyGuard]);

  const handleEdit = () => {
    const currentState = getRowEditState(
      rowKey,
      editableColumns,
      row,
      rowEditStateRef,
      supplyGuard
    );
    const wasEditing = currentState.isEditing;
    if (!currentState.isEditing) {
      // Initialize edit values with current row data
      currentState.editValues = editableColumns.reduce(
        (acc, col) => {
          // Prefer committed value if exists, fall back to row value
          let value = String(
            (currentState.committedValues &&
              currentState.committedValues[col]) ??
              row[col] ??
              ""
          );

          // Format date columns to mm/dd/yy format
          if (col === "order_placed_date" || col === "order_delivery_date") {
            if (value && value !== "null" && value !== "undefined") {
              try {
                let date;
                if (typeof value === "string" && value.includes("/")) {
                  // Already in MM/DD/YY format
                  date = dayjs(value, "MM/DD/YY");
                } else {
                  // ISO format - parse as UTC
                  date = dayjs.utc(value);
                }
                if (date.isValid()) {
                  value = date.format("MM/DD/YY");
                }
              } catch (e) {
                // Keep original value if parsing fails
              }
            }
          }

          acc[col] = value;
          return acc;
        },
        {} as Record<string, string>
      );
    } else {
      // We are toggling from editing -> viewing, so commit only changed values and update row object
      const nextCommitted: Record<string, string> = {
        ...(currentState.committedValues || {}),
      };
      editableColumns.forEach((col) => {
        const prev = nextCommitted[col] ?? String(row[col] ?? "");
        const raw = currentState.editValues[col];
        if (raw !== prev) {
          nextCommitted[col] = raw;
          if (
            col === "quantity" ||
            col === "total_cost" ||
            col === "unit_cost"
          ) {
            const num = Number(String(raw).replace(/,/g, ""));
            (row as any)[col] = isNaN(num) ? raw : num;
          } else if (
            col === "order_placed_date" ||
            col === "order_delivery_date"
          ) {
            // Convert MM/DD/YY back to UTC datetime format
            try {
              const date = dayjs(raw, "MM/DD/YY").utc();
              if (date.isValid()) {
                (row as any)[col] = date.toISOString();
                nextCommitted[col] = date.toISOString();
              } else {
                (row as any)[col] = raw;
              }
            } catch (e) {
              (row as any)[col] = raw;
            }
          } else {
            (row as any)[col] = raw;
          }
        }
      });
      currentState.committedValues = nextCommitted;

      // Only save to persistent storage if there are actual changes
      const hasChanges =
        Object.keys(nextCommitted).length > 0 &&
        Object.keys(nextCommitted).some((col) => {
          const committed = nextCommitted[col];
          const original = currentState.originalValues[col];
          return committed !== original;
        });

      if (hasChanges) {
        supplyGuard?.storeEdit?.(rowKey, {
          committedValues: nextCommitted,
          originalValues: currentState.originalValues,
          approved: false, // New edits are unapproved by default
        });
      } else {
        // Remove from persistent storage if no changes
        supplyGuard?.removeRowEdit?.(rowKey);
      }

      // LEVEL 3 CACHE REMOVED FOR TESTING
      // Bump global edit version so tables rebuild even if already expanded
      // editsVersion++;
      try {
        window.dispatchEvent(
          new CustomEvent("poEditCommitted", { detail: { rowKey } })
        );
        // Also dispatch to trigger guard recompute
        window.dispatchEvent(new CustomEvent("supplyEditsChanged"));
      } catch (_) {}
    }

    // Toggle edit state
    currentState.isEditing = !wasEditing;
    notifyRowListeners(rowKey, rowEditStateRef);
  };

  const handleValueChange = (column: string, value: string) => {
    const currentState = getRowEditState(
      rowKey,
      editableColumns,
      row,
      rowEditStateRef,
      supplyGuard
    );
    currentState.editValues[column] = value;

    // Handle interrelations between quantity, total_cost, and unit_cost
    const quantityStr =
      currentState.editValues["quantity"] ?? String(row["quantity"] ?? "");
    const totalCostStr =
      currentState.editValues["total_cost"] ?? String(row["total_cost"] ?? "");
    const unitCostStr =
      currentState.editValues["unit_cost"] ?? String(row["unit_cost"] ?? "");

    const quantity = Number(quantityStr.replace(/,/g, ""));
    const totalCost = Number(totalCostStr.replace(/,/g, ""));
    const unitCost = Number(unitCostStr.replace(/,/g, ""));

    // If quantity changes, recalculate total_cost = quantity * unit_cost (unit_cost stays constant)
    if (column === "quantity" && !isNaN(quantity) && !isNaN(unitCost)) {
      const newTotalCost = quantity * unitCost;
      currentState.editValues["total_cost"] = String(newTotalCost.toFixed(2));
    }
    // If total_cost changes, recalculate unit_cost = total_cost / quantity (quantity stays constant)
    else if (
      column === "total_cost" &&
      !isNaN(totalCost) &&
      !isNaN(quantity) &&
      quantity !== 0
    ) {
      const newUnitCost = totalCost / quantity;
      currentState.editValues["unit_cost"] = String(newUnitCost.toFixed(2));
    }
    // If unit_cost changes, recalculate total_cost = quantity * unit_cost (quantity stays constant)
    else if (column === "unit_cost" && !isNaN(unitCost) && !isNaN(quantity)) {
      const newTotalCost = quantity * unitCost;
      currentState.editValues["total_cost"] = String(newTotalCost.toFixed(2));
    }

    notifyRowListeners(rowKey, rowEditStateRef);
  };

  return {
    isEditing: state.isEditing,
    editValues: state.editValues,
    committedValues: state.committedValues,
    originalValues: state.originalValues,
    handleEdit,
    handleValueChange,
  };
};

// Individual cell component for editable cells
const EditableCell = React.memo(
  ({
    row,
    column,
    editableColumns,
    isEditing,
    editValue,
    committedValue,
    originalValue,
    isEdited,
    onValueChange,
  }: {
    row: any;
    column: any;
    editableColumns: string[];
    isEditing: boolean;
    editValue: string;
    committedValue: string;
    originalValue: string;
    isEdited: boolean;
    onValueChange: (value: string) => void;
  }) => {
    if (isEditing) {
      // Check if this is a date column
      const isDateColumn =
        column.key === "order_placed_date" ||
        column.key === "order_delivery_date";

      return (
        <Box>
          <CompactTableTextField
            value={editValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              onValueChange(e.target.value);
            }}
            sx={{ width: "100%", maxWidth: "80px" }}
            placeholder={isDateColumn ? "mm/dd/yy" : undefined}
          />
        </Box>
      );
    } else {
      // Use committed value when not editing to show persisted changes
      // Since getRow merges committed values into row, prefer row[column.key]
      // which already has the committed value merged in
      // This ensures display matches what was used for sorting
      let rawValue = row[column.key] ?? committedValue;

      // Apply the same formatting logic used when committing values
      if (column.key === "quantity") {
        const num = Number(String(rawValue).replace(/,/g, ""));
        rawValue = isNaN(num) ? rawValue : num;
      } else if (column.key === "total_cost" || column.key === "unit_cost") {
        const num = Number(String(rawValue).replace(/,/g, ""));
        if (!isNaN(num)) {
          // Format with 2 decimals and locale formatting (commas)
          rawValue = num.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        }
      } else if (
        column.key === "order_placed_date" ||
        column.key === "order_delivery_date"
      ) {
        try {
          let date;
          if (typeof rawValue === "string" && rawValue.includes("/")) {
            // Already in MM/DD/YY format
            date = dayjs(rawValue, "MM/DD/YY");
          } else {
            // ISO format - parse as UTC
            date = dayjs.utc(rawValue);
          }
          if (date.isValid()) rawValue = date.format("MM/DD/YY");
        } catch (_) {}
      }

      const formattedValue = formatCellValue(rawValue, column.cellType);

      // Format original value for tooltip
      let formattedOriginalValue = originalValue;
      if (isEdited && originalValue) {
        if (column.key === "quantity") {
          const num = Number(String(originalValue).replace(/,/g, ""));
          if (!isNaN(num)) {
            formattedOriginalValue = num.toLocaleString();
          }
        } else if (column.key === "total_cost" || column.key === "unit_cost") {
          const num = Number(String(originalValue).replace(/,/g, ""));
          if (!isNaN(num)) {
            formattedOriginalValue = num.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
          }
        } else if (
          column.key === "order_placed_date" ||
          column.key === "order_delivery_date"
        ) {
          try {
            let date;
            if (
              typeof originalValue === "string" &&
              originalValue.includes("/")
            ) {
              date = dayjs(originalValue, "MM/DD/YY");
            } else {
              date = dayjs.utc(originalValue);
            }
            if (date.isValid()) {
              formattedOriginalValue = date.format("MM/DD/YY");
            }
          } catch (_) {}
        }
      }

      const cellContent = (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            minHeight: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: isEdited
              ? (theme) => theme.palette.atomic.orange1
              : "transparent",
            borderRadius: "4px",
            padding: "8px 4px",
            position: "relative",
          }}
        >
          {formattedValue}
          {isEdited && originalValue && (
            <Tooltip title={`Original: ${formattedOriginalValue}`} arrow>
              <Box
                sx={{
                  position: "absolute",
                  top: "1px",
                  right: "1px",
                  width: "16px",
                  height: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  opacity: 0.5,
                  "&:hover": {
                    opacity: 0.8,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 0,
                    height: 0,
                    borderBottom: "8px solid rgba(156, 39, 176, 0.6)",
                    borderLeft: "8px solid transparent",
                    borderTop: "none",
                    borderRight: "none",
                    transform: "rotate(-90deg)",
                  }}
                />
              </Box>
            </Tooltip>
          )}
        </Box>
      );

      return cellContent;
    }
  }
);

// Edit button component
const EditButton = React.memo(
  ({
    row,
    editableColumns,
    isEditing,
    hasEdits,
    onToggleEdit,
    disabled,
    disabledTooltip,
  }: {
    row: any;
    editableColumns: string[];
    isEditing: boolean;
    hasEdits: boolean;
    onToggleEdit: () => void;
    disabled?: boolean;
    disabledTooltip?: string;
  }) => {
    const button = (
      <IconButton
        size="small"
        onClick={disabled ? undefined : onToggleEdit}
        disabled={disabled}
        sx={{ p: 0.5 }}
      >
        {isEditing ? (
          <CheckIcon fontSize="small" />
        ) : (
          <EditIcon fontSize="small" />
        )}
      </IconButton>
    );

    if (disabled && disabledTooltip) {
      return (
        <Tooltip title={disabledTooltip} arrow>
          <span>{button}</span>
        </Tooltip>
      );
    }

    return button;
  }
);
const RevertButton = React.memo(
  ({
    onRevert,
    disabled,
    disabledTooltip,
  }: {
    onRevert: () => void;
    disabled?: boolean;
    disabledTooltip?: string;
  }) => {
    const button = (
      <IconButton
        size="small"
        onClick={disabled ? undefined : onRevert}
        disabled={disabled}
        sx={{ p: 0.5, ml: 0.5 }}
      >
        <RefreshIcon fontSize="small" />
      </IconButton>
    );

    if (disabled && disabledTooltip) {
      return (
        <Tooltip title={disabledTooltip} arrow>
          <span>{button}</span>
        </Tooltip>
      );
    }

    return button;
  }
);

// Wrapper components that use the hook
const EditableCellWrapper = React.memo(
  ({
    row,
    column,
    editableColumns,
    rowIndex,
    rowEditStateRef,
    runAttemptId,
  }: {
    row: any;
    column: any;
    editableColumns: string[];
    rowIndex: number;
    rowEditStateRef: RowEditStateMap;
    runAttemptId: string;
  }) => {
    // Create unique row key based on PO ID + SKU + Location + Supplier + RunAttemptId
    // Use __originalArrowIndex (from getRow) instead of visual rowIndex for consistency with sorting
    const originalIndex = row.__originalArrowIndex ?? rowIndex;
    const rowKey = `${row.purchase_order_id || originalIndex}-${row.sku || "unknown"}-${row.location || "unknown"}-${row.supplier || "unknown"}-${runAttemptId}`;

    const {
      isEditing,
      editValues,
      committedValues,
      originalValues,
      handleValueChange,
    } = useRowEditState(rowKey, editableColumns, row, rowEditStateRef);

    const currentCommitted = committedValues || ({} as Record<string, string>);

    const normalizeForCompare = (key: string, val: any): string => {
      if (key === "order_placed_date" || key === "order_delivery_date") {
        try {
          let date;
          if (typeof val === "string" && val.includes("/")) {
            // Already in MM/DD/YY format
            date = dayjs(val, "MM/DD/YY");
          } else {
            // ISO format - parse as UTC
            date = dayjs.utc(val);
          }
          if (date.isValid()) return date.format("MM/DD/YY");
        } catch (_) {}
        return String(val ?? "");
      }
      if (key === "quantity" || key === "total_cost" || key === "unit_cost") {
        const num = Number(String(val ?? "").replace(/,/g, ""));
        return isNaN(num) ? String(val ?? "") : String(num);
      }
      return String(val ?? "");
    };

    const isEdited = (() => {
      const committedRaw =
        currentCommitted[column.key] ?? String(row[column.key] ?? "");
      const originalRaw =
        originalValues?.[column.key] ?? String(row[column.key] ?? "");
      const committed = normalizeForCompare(column.key, committedRaw);
      const original = normalizeForCompare(column.key, originalRaw);
      return committed !== original;
    })();

    return (
      <EditableCell
        row={row}
        column={column}
        editableColumns={editableColumns}
        isEditing={isEditing}
        editValue={editValues[column.key]}
        committedValue={currentCommitted[column.key]}
        originalValue={
          originalValues?.[column.key] ?? String(row[column.key] ?? "")
        }
        isEdited={isEdited}
        onValueChange={(value: string) => handleValueChange(column.key, value)}
      />
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison: re-render if row identity changed (based on PO + SKU + Location + Supplier)
    const prevId = `${prevProps.row.purchase_order_id}-${prevProps.row.sku}-${prevProps.row.location}-${prevProps.row.supplier}`;
    const nextId = `${nextProps.row.purchase_order_id}-${nextProps.row.sku}-${nextProps.row.location}-${nextProps.row.supplier}`;

    // If row identity changed, re-render
    if (prevId !== nextId) {
      return false; // false means "not equal, re-render"
    }

    // If same row, check if other props changed
    return (
      prevProps.column.key === nextProps.column.key &&
      prevProps.rowIndex === nextProps.rowIndex
    );
  }
);

const EditButtonWrapper = React.memo(
  ({
    row,
    editableColumns,
    rowIndex,
    rowEditStateRef,
    runAttemptId,
  }: {
    row: any;
    editableColumns: string[];
    rowIndex: number;
    rowEditStateRef: RowEditStateMap;
    runAttemptId: string;
  }) => {
    // Create unique row key based on PO ID + SKU + Location + Supplier + RunAttemptId
    // Use __originalArrowIndex (from getRow) instead of visual rowIndex for consistency with sorting
    const originalIndex = row.__originalArrowIndex ?? rowIndex;
    const rowKey = `${row.purchase_order_id || originalIndex}-${row.sku || "unknown"}-${row.location || "unknown"}-${row.supplier || "unknown"}-${runAttemptId}`;
    const { isEditing, committedValues, originalValues, handleEdit } =
      useRowEditState(rowKey, editableColumns, row, rowEditStateRef);

    // Get context for removeRowEdit function
    const supplyGuard = useContext(SupplyWalkEditsContext);

    // Local state to track approval status for instant UI updates
    const [isApproved, setIsApproved] = useState(row.approved || false);

    // Listen for approval toggle events for this specific row
    useEffect(() => {
      const handleApprovalToggle = (e: Event) => {
        const detail = (e as CustomEvent).detail as {
          rowKey: string;
          checked: boolean;
        };
        if (detail.rowKey === rowKey) {
          setIsApproved(detail.checked);
        }
      };

      window.addEventListener(
        "poApprovalToggled",
        handleApprovalToggle as EventListener
      );
      return () => {
        window.removeEventListener(
          "poApprovalToggled",
          handleApprovalToggle as EventListener
        );
      };
    }, [rowKey]);

    // Sync with ref state on mount/changes (for initial load or external updates)
    useEffect(() => {
      setIsApproved(row.approved || false);
    }, [rowKey, row.approved]);

    const normalizeForCompare = (key: string, val: any): string => {
      if (key === "order_placed_date" || key === "order_delivery_date") {
        try {
          let date;
          if (typeof val === "string" && val.includes("/")) {
            // Already in MM/DD/YY format
            date = dayjs(val, "MM/DD/YY");
          } else {
            // ISO format - parse as UTC
            date = dayjs.utc(val);
          }
          if (date.isValid()) return date.format("MM/DD/YY");
        } catch (_) {}
        return String(val ?? "");
      }
      if (key === "quantity" || key === "total_cost" || key === "unit_cost") {
        const num = Number(String(val ?? "").replace(/,/g, ""));
        return isNaN(num) ? String(val ?? "") : String(num);
      }
      return String(val ?? "");
    };

    const hasEdits = editableColumns.some((col) => {
      const committedRaw =
        (committedValues && committedValues[col]) ?? String(row[col] ?? "");
      const originalRaw =
        (originalValues && originalValues[col]) ?? String(row[col] ?? "");
      const committed = normalizeForCompare(col, committedRaw);
      const original = normalizeForCompare(col, originalRaw);

      return committed !== original;
    });

    const handleRevert = () => {
      const state = getRowEditState(
        rowKey,
        editableColumns,
        row,
        rowEditStateRef,
        supplyGuard
      );
      const nextCommitted: Record<string, string> = {
        ...state.committedValues,
      };
      editableColumns.forEach((col) => {
        let raw = state.originalValues[col];
        nextCommitted[col] = raw;

        if (col === "quantity" || col === "total_cost" || col === "unit_cost") {
          const num = Number(String(raw).replace(/,/g, ""));
          (row as any)[col] = isNaN(num) ? raw : num;
        } else if (
          col === "order_placed_date" ||
          col === "order_delivery_date"
        ) {
          // For date columns, ensure we store UTC datetime format
          try {
            let parsed;
            if (typeof raw === "string" && raw.includes("/")) {
              // Already in MM/DD/YY format - convert to UTC
              parsed = dayjs(raw, "MM/DD/YY").utc();
            } else {
              // ISO format - parse as UTC
              parsed = dayjs.utc(raw);
            }
            if (parsed.isValid()) {
              (row as any)[col] = parsed.toISOString();
              nextCommitted[col] = parsed.toISOString();
            } else {
              (row as any)[col] = raw;
            }
          } catch (_) {
            (row as any)[col] = raw;
          }
        } else {
          (row as any)[col] = raw;
        }
      });
      state.committedValues = nextCommitted;

      // Clear from persistent storage when reverting
      if (supplyGuard?.removeRowEdit) {
        supplyGuard.removeRowEdit(rowKey);
      }

      // Dispatch undo event to notify detail tables
      try {
        window.dispatchEvent(
          new CustomEvent("poEditUndone", { detail: { rowKey } })
        );
      } catch (_) {}

      // After revert, if no differences remain, hide revert button by notifying
      notifyRowListeners(rowKey, rowEditStateRef);
    };

    return (
      <Box
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <EditButton
          row={row}
          editableColumns={editableColumns}
          isEditing={isEditing}
          hasEdits={hasEdits}
          onToggleEdit={handleEdit}
          disabled={isApproved}
          disabledTooltip={
            isApproved ? " to edit approved POs, unapprove them" : undefined
          }
        />
        {hasEdits && (
          <RevertButton
            onRevert={handleRevert}
            disabled={isApproved}
            disabledTooltip={
              isApproved ? " to edit approved POs, unapprove them" : undefined
            }
          />
        )}
      </Box>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison: re-render if row identity changed (based on PO + SKU + Location + Supplier)
    const prevId = `${prevProps.row.purchase_order_id}-${prevProps.row.sku}-${prevProps.row.location}-${prevProps.row.supplier}`;
    const nextId = `${nextProps.row.purchase_order_id}-${nextProps.row.sku}-${nextProps.row.location}-${nextProps.row.supplier}`;

    // If row identity changed, re-render
    if (prevId !== nextId) {
      return false; // false means "not equal, re-render"
    }

    // If same row, check if other props changed
    return prevProps.rowIndex === nextProps.rowIndex;
  }
);

// --- PO EDIT DATA (Copy of getFlatTableData for parent table) ---
export function getPOEditData({
  arrowTable,
  columns,
  sortConfig = [],
  onSort,
  columnSchema,
  setSortConfig,
  firstMount,
  showActions = true,
  columnHeaderMap,
  rowEditStateRef,
  runAttemptId,
}: {
  arrowTable: arrow.Table;
  columns: arrow.Field<any>[];
  sortConfig?: Array<{ key: string; direction: "asc" | "desc" }>;
  onSort?: (key: string, e: React.MouseEvent<HTMLTableCellElement>) => void;
  columnSchema?: Record<
    string,
    { dtype: string; min?: string; max?: string; unique_values?: string[] }
  >;
  setSortConfig?: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  firstMount?: React.MutableRefObject<boolean>;
  showActions?: boolean;
  columnHeaderMap?: Record<string, string>;
  rowEditStateRef: RowEditStateMap;
  runAttemptId: string;
}) {
  // LEVEL 3 CACHE REMOVED FOR TESTING
  // const numRows = arrowTable.numRows;

  // Get purchase_order_id vector for efficient dummy row filtering (avoid expensive toArray())
  const purchaseOrderIdVector = arrowTable.getChild("purchase_order_id");
  const nonDummyRowIndices: number[] = [];

  if (purchaseOrderIdVector) {
    for (let i = 0; i < arrowTable.numRows; i++) {
      const poId = purchaseOrderIdVector.get(i);
      if (poId !== "DUMMY" && poId != null) {
        nonDummyRowIndices.push(i);
      }
    }
  } else {
    // Fallback: if vector not available, use all indices
    for (let i = 0; i < arrowTable.numRows; i++) {
      nonDummyRowIndices.push(i);
    }
  }

  // Get status column vector for efficient status lookup (status is filtered out of displayColumns)
  const statusVector = arrowTable.getChild("status");

  // Helper function to get status value from Arrow table at a specific index
  const getStatusAt = (index: number): string => {
    if (statusVector && index >= 0 && index < statusVector.length) {
      const statusValue = statusVector.get(index);
      return statusValue != null ? String(statusValue) : "unknown";
    }
    return "unknown";
  };

  // Convert to array only for getEffectiveValue function (needed for sorting with edits)
  // This is still expensive but only done once, and we avoid it for status lookups
  const allRowsArray = arrowTable.toArray();

  // Define editable columns
  const editableColumns = [
    "quantity",
    "total_cost",
    "unit_cost",
    "order_placed_date",
    "order_delivery_date",
  ];

  // Filter out count columns and status column from display (they're only used for filtering/counts)
  // Do this BEFORE sorting so we can use displayColumns for lookups
  const displayColumns = columns.filter(
    (col) =>
      ![
        "status",
        "approved_count",
        "delivered_count",
        "open_count",
        "needs_review_count",
      ].includes(col.name)
  );

  // Use only non-dummy row indices
  let visibleRows = nonDummyRowIndices;

  if (sortConfig && sortConfig.length > 0) {
    // Check if any editable column or date column is being sorted
    // Use custom sort for editable columns (to account for edited values) and date columns (to ensure proper date comparison)
    const dateColumns = ["order_placed_date", "order_delivery_date"];
    const hasEditableSort = sortConfig.some((s) =>
      editableColumns.includes(s.key)
    );
    const hasDateSort = sortConfig.some((s) => dateColumns.includes(s.key));

    // Use custom sort when sorting by editable columns or date columns
    if (hasEditableSort || hasDateSort) {
      // Custom sort that accounts for edited values
      const getEffectiveValue = (rowIndex: number, columnKey: string): any => {
        const row = allRowsArray[rowIndex];
        if (!row) {
          return undefined;
        }

        let value: any = undefined;

        // For editable columns, check for edited values first
        if (editableColumns.includes(columnKey)) {
          const rowKey = `${row.purchase_order_id || rowIndex}-${row.sku || "unknown"}-${row.location || "unknown"}-${row.supplier || "unknown"}-${runAttemptId}`;
          const editState = rowEditStateRef.get(rowKey);
          if (editState?.committedValues?.[columnKey] !== undefined) {
            value = editState.committedValues[columnKey];
          }
        }

        // If no edited value, use original value from Arrow table
        if (value === undefined) {
          // Try to get from row data first (this is the most reliable)
          if (row[columnKey] !== undefined) {
            value = row[columnKey];
          } else {
            // Fallback to Arrow table vector
            const originalColIdx = columns.findIndex(
              (col) => col.name === columnKey
            );
            if (originalColIdx >= 0) {
              const vector = arrowTable.getChildAt(originalColIdx);
              if (vector) {
                value = vector.get(rowIndex);
              }
            }
          }
        }

        // Convert to appropriate type for comparison
        if (
          columnKey === "quantity" ||
          columnKey === "total_cost" ||
          columnKey === "unit_cost"
        ) {
          // Handle both edited values (might be strings) and original values (might be numbers)
          const num = Number(String(value ?? "").replace(/,/g, ""));
          return isNaN(num) ? (value ?? 0) : num;
        }
        // For dates, convert to comparable format
        if (
          columnKey === "order_placed_date" ||
          columnKey === "order_delivery_date"
        ) {
          if (value == null) return undefined;
          try {
            // Handle both ISO strings and date objects
            let date;
            if (typeof value === "string" && value.includes("/")) {
              // Try multiple date formats to handle non-padded dates (e.g., "01/1/26" or "01/01/26")
              // Try with strict mode first, then without
              date = dayjs(value, "MM/DD/YY", true).utc();
              if (!date.isValid()) {
                date = dayjs(value, "M/D/YY", true).utc();
              }
              if (!date.isValid()) {
                date = dayjs(value, "MM/D/YY", true).utc();
              }
              if (!date.isValid()) {
                date = dayjs(value, "M/DD/YY", true).utc();
              }
              // Fallback to non-strict parsing
              if (!date.isValid()) {
                date = dayjs(value, "MM/DD/YY").utc();
              }
            } else {
              date = dayjs.utc(value);
            }

            return date.isValid() ? date.valueOf() : value;
          } catch {
            return value;
          }
        }

        return value;
      };

      // Custom sort with edited values - handles both editable and non-editable columns
      visibleRows = [...nonDummyRowIndices].sort((a, b) => {
        for (let i = 0; i < sortConfig.length; i++) {
          const { key, direction } = sortConfig[i];
          let aVal = getEffectiveValue(a, key);
          let bVal = getEffectiveValue(b, key);

          // Ensure consistent type comparison for numeric columns
          if (
            key === "quantity" ||
            key === "total_cost" ||
            key === "unit_cost"
          ) {
            // Both values should already be numbers from getEffectiveValue, but ensure it
            aVal =
              typeof aVal === "number"
                ? aVal
                : Number(String(aVal || "").replace(/,/g, "")) || 0;
            bVal =
              typeof bVal === "number"
                ? bVal
                : Number(String(bVal || "").replace(/,/g, "")) || 0;
          }

          // Handle null/undefined values
          if (aVal == null && bVal == null) continue;
          if (aVal == null || aVal === undefined) {
            return direction === "asc" ? -1 : 1;
          }
          if (bVal == null || bVal === undefined) {
            return direction === "asc" ? 1 : -1;
          }

          // Compare values
          if (aVal < bVal) {
            return direction === "asc" ? -1 : 1;
          }
          if (aVal > bVal) {
            return direction === "asc" ? 1 : -1;
          }
        }
        return 0;
      });
    } else {
      // Use standard Arrow table sorting for non-editable columns
      visibleRows = getSortedRowIndices(arrowTable, displayColumns, sortConfig);
      // Filter out dummy rows from sorted indices too
      visibleRows = visibleRows.filter(
        (i) => allRowsArray[i].purchase_order_id !== "DUMMY"
      );
    }
  }

  const muiColumns = buildMUIColumns({
    columns: displayColumns,
    sortConfig,
    onSort,
    columnSchema,
    columnHeaderMap,
  });

  const baseGetRow = makeGetRowFromArrowTableWithIndices(
    arrowTable,
    displayColumns,
    visibleRows
  );

  // Wrap getRow to apply committed values so row data matches what's displayed
  const getRow = (rowIndex: number) => {
    const baseRow = baseGetRow(rowIndex);
    if (!baseRow) return baseRow;

    // Get original Arrow table index for consistent rowKey computation
    const originalArrowIndex = visibleRows[rowIndex];

    // Add __originalArrowIndex to baseRow for consistent rowKey computation in EditableCellWrapper
    (baseRow as any).__originalArrowIndex = originalArrowIndex;

    // Get status from Arrow table vector directly (avoid array lookup)
    // Status is filtered out of displayColumns but needed for subscription key
    const rowStatus =
      getStatusAt(originalArrowIndex) || baseRow?.status || "unknown";

    // Apply committed values to match what EditableCellWrapper displays
    // Use originalArrowIndex (not rowIndex) to keep rowKey consistent with sorting
    const rowKey = `${baseRow.purchase_order_id || originalArrowIndex}-${baseRow.sku || "unknown"}-${baseRow.location || "unknown"}-${baseRow.supplier || "unknown"}-${rowStatus}-${runAttemptId}`;
    const editState = rowEditStateRef.get(rowKey);

    if (editState?.committedValues) {
      // Merge committed values into the row data
      editableColumns.forEach((colKey) => {
        if (editState.committedValues[colKey] !== undefined) {
          // Convert committed value to appropriate type
          const committedValue = editState.committedValues[colKey];
          if (
            colKey === "quantity" ||
            colKey === "total_cost" ||
            colKey === "unit_cost"
          ) {
            const num = Number(String(committedValue).replace(/,/g, ""));
            (baseRow as any)[colKey] = isNaN(num) ? committedValue : num;
          } else {
            (baseRow as any)[colKey] = committedValue;
          }
        }
      });
    }

    return baseRow;
  };

  // Calculate totals using the reusable function
  const columnTotals = calculateColumnTotals({
    arrowTable,
    columns: muiColumns,
    hasTableId: false,
  });

  // Enhanced columns with edit functionality
  const enhancedColumns = muiColumns.map((column) => {
    if (editableColumns.includes(column.key)) {
      return {
        ...column,
        renderCell: (row: any, rowIndex: number) => {
          return (
            <EditableCellWrapper
              row={row}
              column={column}
              editableColumns={editableColumns}
              rowIndex={rowIndex}
              rowEditStateRef={rowEditStateRef}
              runAttemptId={runAttemptId}
            />
          );
        },
      };
    }
    return column;
  });

  // Add edit column at the end only if showActions is true
  const columnsWithEdit = showActions
    ? [
        ...enhancedColumns,
        {
          key: "__actions",
          label: "actions",
          width: 50,
          align: "center" as const,
          sticky: false,
          sortable: false,
          onSort: undefined,
          renderCell: (row: any, rowIndex: number) => {
            return (
              <EditButtonWrapper
                row={row}
                editableColumns={editableColumns}
                rowIndex={rowIndex}
                rowEditStateRef={rowEditStateRef}
                runAttemptId={runAttemptId}
              />
            );
          },
        },
      ]
    : enhancedColumns;

  const result = {
    columns: columnsWithEdit,
    getRow,
    rowCount: visibleRows.length,
    arrowTable,
    columnTotals,
  };

  return result;
}

export function getSupplyWalkEditTableData({
  arrowTable,
  columns: arrowColumns,
  viewParams,
  columnSchema,
  theme,
  firstMount,
}: {
  arrowTable: arrow.Table;
  columns: arrow.Field<any>[];
  viewParams: any;
  columnSchema?: Record<
    string,
    { dtype: string; min?: string; max?: string; unique_values?: string[] }
  >;
  theme: Theme;
  firstMount?: React.MutableRefObject<boolean>;
}) {
  if (
    !arrowTable ||
    !arrowColumns ||
    !viewParams?.segments ||
    !viewParams?.dateHierarchy
  ) {
    return null;
  }

  // Get unique dates efficiently using Arrow vectors directly
  const dateField = viewParams.dateHierarchy;
  const dateVector = arrowTable.getChild(dateField);
  if (!dateVector) {
    return null;
  }

  const dateSet = new Set<number>();
  for (let i = 0; i < dateVector.length; i++) {
    const dateValue = dateVector.get(i);
    if (dateValue != null) {
      dateSet.add(dayjs.utc(dateValue).valueOf());
    }
  }
  const allDates = Array.from(dateSet)
    .sort((a, b) => a - b)
    .map((ts) => dayjs.utc(ts));

  // Get all necessary vectors once
  const segmentVectors = viewParams.segments.map((segment: string) =>
    arrowTable.getChild(segment)
  );
  const measureVector = arrowTable.getChild("measure");
  const valueVector = arrowTable.getChild("value");
  const segmentationRankVector =
    arrowTable.getChild("segmentation_rank") ||
    arrowTable.getChild("segmentationRank");
  const measureRankVector =
    arrowTable.getChild("measure_rank") || arrowTable.getChild("metric_rank");

  if (
    !measureVector ||
    !valueVector ||
    segmentVectors.some((v: arrow.Vector | null) => !v)
  ) {
    return null;
  }

  // Build efficient lookup maps in one pass (O(n) upfront, O(1) lookup)
  const segMeasureToRows = new Map<string, number[]>();
  const segMeasureKeys: string[] = [];
  const segMeasureData = new Map<
    string,
    {
      segmentationKey: string;
      measure: string;
      segmentValues: string[];
      segmentationRank: number;
      measureRank: number;
    }
  >();

  // Single pass through Arrow data to build all lookup structures
  for (let i = 0; i < arrowTable.numRows; i++) {
    const segmentValues = segmentVectors.map((vector: arrow.Vector | null) =>
      String(vector!.get(i) || "")
    );
    const segmentationKey = segmentValues.join(" | ");
    const measure = String(measureVector.get(i) || "");
    const combinedKey = `${segmentationKey}||${measure}`;

    // Track arrow row indices for this segmentation+measure
    if (!segMeasureToRows.has(combinedKey)) {
      segMeasureToRows.set(combinedKey, []);
      segMeasureKeys.push(combinedKey);

      // Store metadata for this combination
      segMeasureData.set(combinedKey, {
        segmentationKey,
        measure,
        segmentValues,
        segmentationRank: Number(segmentationRankVector?.get(i) || 0),
        measureRank: Number(measureRankVector?.get(i) || 0),
      });
    }
    segMeasureToRows.get(combinedKey)!.push(i);
  }

  // Sort by ranks for consistent ordering
  segMeasureKeys.sort((a, b) => {
    const dataA = segMeasureData.get(a)!;
    const dataB = segMeasureData.get(b)!;
    if (dataA.segmentationRank !== dataB.segmentationRank) {
      return dataA.segmentationRank - dataB.segmentationRank;
    }
    return dataA.measureRank - dataB.measureRank;
  });

  // Build the matrix data structure
  const matrixData: any[] = [];
  const dateColumns: string[] = [];

  // Create date columns
  allDates.forEach((date) => {
    const dateKey = shortFormatDateString(date);
    dateColumns.push(dateKey);
  });

  // Process each segmentation+measure combination
  segMeasureKeys.forEach((combinedKey) => {
    const data = segMeasureData.get(combinedKey)!;
    const rowIndices = segMeasureToRows.get(combinedKey)!;

    // Create row object with segmentation values
    const row: any = {};
    viewParams.segments.forEach((segment: string, idx: number) => {
      row[segment] = data.segmentValues[idx];
    });
    row.measure = data.measure;
    row.segmentationKey = data.segmentationKey;

    // Build date value map for this row (using original structure)
    const dateValues: Record<string, any> = {};
    rowIndices.forEach((rowIdx) => {
      const dateValue = dayjs.utc(dateVector.get(rowIdx)).valueOf();
      const value = valueVector.get(rowIdx);
      dateValues[dateValue] = value;
    });
    row.dateValues = dateValues;

    // Also add date columns directly to row for compatibility
    allDates.forEach((date) => {
      const dateKey = `${date.valueOf()}`;
      row[dateKey] = dateValues[date.valueOf()] || "";
    });

    matrixData.push(row);
  });

  // Extract actual SKU and location values for column header
  const firstRow = matrixData.length > 0 ? matrixData[0] : null;
  const actualSegmentation = firstRow?.segmentationKey || "Unknown";

  // Create columns for the table
  const tableColumns = [
    {
      key: "measure",
      label: actualSegmentation, // Show actual SKU | location values
      width: 200,
      sticky: true,
      cellType: new arrow.Utf8(),
      headerType: new arrow.Utf8(),
      renderCell: (row: any, rowIndex: number) => {
        return (
          <Box
            sx={{
              height: "40px",
              display: "flex",
              alignItems: "center",
              p: 1,
            }}
          >
            {row.measure}
          </Box>
        );
      },
    },
    ...allDates.map((date) => ({
      key: `${date.valueOf()}`,
      cellType: new arrow.Float64(),
      headerType: new arrow.Timestamp(arrow.TimeUnit.MILLISECOND),
      label: shortFormatDateString(date),
      renderCell: (row: any, rowIndex: number) => {
        const value = row.dateValues[date.valueOf()];
        const numericString = String(value ?? "").replace(/,/g, "");
        const numericValue = Number(numericString);

        let highlightColor = undefined;

        // Check for negative inventory - RED (highest priority)
        const measureLower = row.measure.toLowerCase();
        const isStartingInventory = measureLower === "starting inventory";
        const isEndingInventory = measureLower === "ending inventory";

        if (
          (isStartingInventory || isEndingInventory) &&
          numericString !== "" &&
          !isNaN(numericValue) &&
          numericValue < 0
        ) {
          highlightColor = theme.palette.atomic.red1; // Red background for negative inventory
        }
        // Check for purchase order arrivals - YELLOW (any type: total, open, in review, approved)
        else if (measureLower.includes("purchase order arrivals")) {
          highlightColor = theme.palette.atomic.orange1; // Yellow/orange background
        }
        // Check for purchase order to place with positive value - YELLOW
        else if (
          measureLower === "purchase order to place" &&
          numericString !== "" &&
          !isNaN(numericValue) &&
          numericValue > 0
        ) {
          highlightColor = theme.palette.atomic.orange1; // Yellow/orange background
        }

        // Color code weeks of supply (only if not already highlighted)
        let cellColor = undefined;
        if (!highlightColor) {
          const isWOS =
            measureLower ===
              getWOSMeasureKey(
                "starting",
                viewParams?.dateHierarchy || ""
              ).toLowerCase() ||
            measureLower ===
              getWOSMeasureKey(
                "ending",
                viewParams?.dateHierarchy || ""
              ).toLowerCase() ||
            measureLower.includes("target weeks of supply");

          // Skip coloring for target weeks of supply
          const isTargetWOS = measureLower.includes("target weeks of supply");

          if (
            isWOS &&
            !isTargetWOS &&
            numericString !== "" &&
            !isNaN(numericValue)
          ) {
            // Find target WOS within the same segmentation
            let targetWOS = null;
            const targetMeasureName = getWOSMeasureKey(
              "target",
              viewParams?.dateHierarchy || ""
            );

            // Find target measure in current segmentation by looking at nearby rows
            for (let i = 0; i < matrixData.length; i++) {
              const checkRow = matrixData[i];
              if (
                checkRow &&
                checkRow.segmentationKey &&
                checkRow.segmentationKey === row.segmentationKey &&
                checkRow.measure &&
                checkRow.measure.toLowerCase() ===
                  targetMeasureName.toLowerCase()
              ) {
                const targetValRaw = checkRow.dateValues[date.valueOf()];
                if (
                  targetValRaw !== undefined &&
                  targetValRaw !== null &&
                  targetValRaw !== ""
                ) {
                  const targetString =
                    typeof targetValRaw === "string"
                      ? targetValRaw.replace(/,/g, "")
                      : String(targetValRaw);
                  const parsedTarget = Number(targetString);
                  if (!isNaN(parsedTarget)) {
                    targetWOS = parsedTarget;
                  }
                }
                break;
              }
            }

            if (targetWOS !== null && !isNaN(targetWOS)) {
              cellColor = getSupplyCellBackgroundColor({
                value: numericValue,
                targetValue: targetWOS,
                theme,
              });
            }
          }
        }

        return (
          <Box
            sx={{
              p: 1,
              backgroundColor: highlightColor || cellColor,
              textAlign: "center",
              fontWeight: "normal",
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {value ?? ""}
          </Box>
        );
      },
    })),
  ];

  return {
    columns: tableColumns,
    getRow: (index: number) => matrixData[index],
    rowCount: matrixData.length,
    arrowTable,
    columnTotals: {},
  };
}

export interface GetMatrixParams {
  arrowTable: arrow.Table;
  columns: arrow.Field<any>[];
  sortConfig: Array<{ key: string; direction: "asc" | "desc"; index: number }>;
  setSortConfig: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  columnSchema?: Record<
    string,
    { dtype: string; min?: string; max?: string; unique_values?: string[] }
  >;
  expandedGroups?: Set<string>;
  viewParams?: any;
  columnHeaderMap?: Record<string, string>;
  firstMount?: React.MutableRefObject<boolean>;
  // Add run information for cache invalidation
  mainRun?: any;
  lastSuccessfulRun?: any;
}

// Self-contained checkbox component that doesn't trigger parent re-renders
const ApprovedCheckbox = React.memo(
  ({
    rowKey,
    rowData,
    syncVersion,
    onApprovedToggle,
    runAttemptId,
  }: {
    rowKey: string;
    rowData: any;
    syncVersion: number;
    onApprovedToggle: (rowKey: string, rowData: any, checked: boolean) => void;
    runAttemptId?: string;
  }) => {
    // Local state for this checkbox only
    const [isChecked, setIsChecked] = useState(() => {
      return rowData?.approved || false;
    });

    // Sync local state with data when syncVersion changes (on load or rollback)
    // Also reset when run changes to ensure fresh state
    useEffect(() => {
      setIsChecked(rowData?.approved || false);
    }, [rowKey, syncVersion, rowData?.approved, runAttemptId]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;
      // Update local state immediately for instant UI feedback
      setIsChecked(checked);
      // Notify parent (doesn't trigger parent re-render)
      onApprovedToggle(rowKey, rowData, checked);
      // Dispatch event to notify EditButtonWrapper components
      try {
        window.dispatchEvent(
          new CustomEvent("poApprovalToggled", {
            detail: { rowKey, checked },
          })
        );
      } catch (_) {}
    };

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <Checkbox
          checked={isChecked}
          onChange={handleChange}
          size="small"
          sx={{ p: 0 }}
        />
      </Box>
    );
  }
);

ApprovedCheckbox.displayName = "ApprovedCheckbox";

// Add custom comparison to ensure re-render when run changes
const ApprovedCheckboxWithRunComparison = React.memo(
  ApprovedCheckbox,
  (prevProps, nextProps) => {
    // Re-render if runAttemptId changes (run switch)
    if (prevProps.runAttemptId !== nextProps.runAttemptId) {
      return false; // false means "re-render"
    }

    // Re-render if rowData.approved changes
    if (prevProps.rowData?.approved !== nextProps.rowData?.approved) {
      return false;
    }

    // Re-render if syncVersion changes
    if (prevProps.syncVersion !== nextProps.syncVersion) {
      return false;
    }

    // Don't re-render for other changes
    return true;
  }
);

// LEVEL 3 CACHE REMOVED FOR TESTING
// Memo cache for matrix building to avoid whole-table rebuilds
// const matrixMemoRef = { current: null as { key: string; result: any } | null };

// Cached child tables keyed by groupKey; ref avoids parent re-renders
const childTablesRef = {
  current: {} as Record<
    string,
    {
      loading: boolean;
      error: string | null;
      parsed: any | null;
    }
  >,
};

// Track the current run to clear cache when it changes
let lastRunAttemptId: string | null = null;

// LEVEL 3 CACHE REMOVED FOR TESTING
// Add event listener to force cache invalidation on run changes
// if (typeof window !== "undefined") {
//   window.addEventListener("runChanged", () => {
//     // Clear all caches when run changes
//     matrixMemoRef.current = null;
//     childTablesRef.current = {};
//     lastRunAttemptId = null;
//     // Reset edits version to force matrix rebuild
//     editsVersion = 0;
//   });
// }

export function getSupplyWalkEditableViewMatrix({
  arrowTable,
  columns,
  sortConfig,
  setSortConfig,
  columnSchema,
  expandedGroups,
  viewParams,
  columnHeaderMap,
  firstMount,
  mainRun,
  lastSuccessfulRun,
  NestedDetailsComponent,
  currentStatus,
  rowEditStateRef,
  syncVersion = 0,
  onApprovedToggle,
  supplyGuard,
}: GetMatrixParams & {
  NestedDetailsComponent: React.ComponentType<any>;
  currentStatus?: string;
  rowEditStateRef: RowEditStateMap;
  syncVersion?: number;
  onApprovedToggle?: (rowKey: string, rowData: any, checked: boolean) => void;
  supplyGuard: any;
}) {
  // Clear cache when run changes to prevent stale data
  const currentRunAttemptId =
    mainRun?.runSettings?.runAttemptId ||
    lastSuccessfulRun?.runSettings?.runAttemptId ||
    "no-run";
  if (lastRunAttemptId !== null && lastRunAttemptId !== currentRunAttemptId) {
    childTablesRef.current = {};
  }
  lastRunAttemptId = currentRunAttemptId;

  // Get status column vector directly (avoid expensive toArray() conversion)
  // Status is filtered out of displayColumns but needed for subscription key
  const statusVector = arrowTable.getChild("status");

  // Helper function to get status value from Arrow table at a specific index
  const getStatusAt = (index: number): string => {
    if (statusVector && index >= 0 && index < statusVector.length) {
      const statusValue = statusVector.get(index);
      return statusValue != null ? String(statusValue) : "unknown";
    }
    return "unknown";
  };

  // Create computeItemKey function using the same subscription key format
  const computeItemKey = (index: number, row: any): string => {
    // For detail rows, use a different key format
    if (row.type === "detail") {
      const parent = row.__parentRow;
      if (parent) {
        const originalIndex = parent.__originalArrowIndex ?? index;
        // Get status from Arrow table directly (avoid array conversion)
        const rowStatus =
          getStatusAt(originalIndex) || parent?.status || "unknown";
        return `detail-${parent.purchase_order_id || originalIndex}-${parent.sku || "unknown"}-${parent.location || "unknown"}-${parent.supplier || "unknown"}-${rowStatus}-${currentRunAttemptId}`;
      }
      return `detail-${index}`;
    }

    // For regular rows, use the subscription key format
    const originalIndex = row.__originalArrowIndex ?? index;
    // Get status from Arrow table directly (avoid array conversion)
    const rowStatus = getStatusAt(originalIndex) || row?.status || "unknown";
    return `${row.purchase_order_id || originalIndex}-${row.sku || "unknown"}-${row.location || "unknown"}-${row.supplier || "unknown"}-${rowStatus}-${currentRunAttemptId}`;
  };

  // LEVEL 3 CACHE REMOVED FOR TESTING
  // Create a hash of the actual data content to detect data changes
  // const dataHash = arrowTable
  //   ? JSON.stringify(
  //       arrowTable
  //         .toArray()
  //         .slice(0, 5)
  //         .map((row: any) => ({
  //           po: row.purchase_order_id,
  //           sku: row.sku,
  //           location: row.location,
  //           approved: row.approved,
  //         }))
  //     )
  //   : "no-data";

  // LEVEL 3 CACHE REMOVED FOR TESTING
  // const key = [
  //   arrowTable?.numRows,
  //   columns?.length,
  //   JSON.stringify(sortConfig || []),
  //   Array.from(expandedGroups || new Set()).join("|"),
  //   editsVersion,
  //   currentStatus,
  //   // Include run information to ensure cache invalidation when run changes
  //   mainRun?.runSettings?.runAttemptId ||
  //     lastSuccessfulRun?.runSettings?.runAttemptId ||
  //     "no-run",
  //   // Include data content hash to detect data changes
  //   dataHash,
  // ].join("::");

  // Checkbox cells manage their own state, so no matrix rebuild needed

  // if (matrixMemoRef.current?.key !== key) {
  // }

  // if (matrixMemoRef.current?.key === key) {
  //   // Reuse previous matrix result to avoid table rebuild
  //   return matrixMemoRef.current.result;
  // }

  // FORCE REBUILD EVERY TIME - NO CACHING

  // For "Approved" status, the transform now handles loading approved_pos data
  // So we just use the regular PO edit data flow for all statuses

  const baseTableData = getPOEditData({
    arrowTable,
    columns,
    sortConfig: sortConfig.map(({ key, direction }) => ({
      key,
      direction,
    })),
    onSort: (key: string, e: React.MouseEvent<HTMLTableCellElement>) => {
      setSortConfig((prev) => {
        const isShiftPressed = e?.shiftKey || false;
        const existingIndex = prev.findIndex((s) => s.key === key);
        const existingSort = existingIndex >= 0 ? prev[existingIndex] : null;
        const baseConfig = isShiftPressed ? [...prev] : [];
        if (existingSort) {
          const newDirection = existingSort.direction === "asc" ? "desc" : null;
          if (newDirection) {
            const index = isShiftPressed ? existingIndex : 0;
            const newConfig = [
              ...baseConfig.slice(0, index),
              {
                key,
                direction: newDirection,
                index,
              } as any,
              ...baseConfig.slice(index + 1),
            ];
            return newConfig;
          }
          const filteredConfig = baseConfig.filter(
            (_, idx) => idx !== existingIndex
          );
          return filteredConfig;
        }
        const newConfig = [
          ...baseConfig,
          { key, direction: "asc", index: baseConfig.length } as any,
        ];
        return newConfig;
      });
    },
    columnSchema,
    setSortConfig,
    firstMount,
    showActions: currentStatus === "Needs Review",
    columnHeaderMap,
    rowEditStateRef,
    runAttemptId: currentRunAttemptId,
  });

  const filteredBaseTableData = baseTableData;

  // Remove the backend 'approved' column to avoid duplication with frontend checkbox
  const columnsWithoutBackendApproved = filteredBaseTableData.columns.filter(
    (c) => c.key !== "approved"
  );

  // Update the filtered data to use the cleaned columns
  const cleanedBaseTableData = {
    ...filteredBaseTableData,
    columns: columnsWithoutBackendApproved,
  };

  // For "Approved" status, return flat table without expand/details functionality
  // For other statuses, only show expand/details for "Needs Review"
  if (currentStatus === "Approved") {
    return {
      columns: cleanedBaseTableData.columns,
      getRow: (index: number) => cleanedBaseTableData.getRow(index),
      rowCount: cleanedBaseTableData.rowCount,
      columnTotals: cleanedBaseTableData.columnTotals,
      computeItemKey,
    };
  }

  // If not in "Needs Review" status, do NOT include expand/details
  // Return the flat parent table only
  if (currentStatus !== "Needs Review") {
    return {
      columns: cleanedBaseTableData.columns,
      getRow: (index: number) => cleanedBaseTableData.getRow(index),
      rowCount: cleanedBaseTableData.rowCount,
      columnTotals: cleanedBaseTableData.columnTotals,
      computeItemKey,
    };
  }

  // Add an expand column, approved checkbox column (only for Needs Review), and a details column
  const expandAndCheckboxColumns: MUIFlatTableColumn[] = [
    {
      key: "__expand",
      label: "",
      isExpandColumn: true,
      width: 36,
      sticky: true,
      align: "center",
      sortable: false,
      onSort: undefined,
    },
  ];

  // Add checkbox column only for "Needs Review" status
  if (currentStatus === "Needs Review") {
    expandAndCheckboxColumns.push({
      key: "approved",
      label: "approved",
      width: 80,
      sticky: true,
      align: "center",
      sortable: false,
      onSort: undefined,
      renderCell: (row: any) => {
        if (row.type === "detail") return null;

        const rowKey = `${row.purchase_order_id}-${row.sku}-${row.location}-${row.supplier}-${currentRunAttemptId}`;
        // LEVEL 3 CACHE REMOVED FOR TESTING
        // const currentRunId =
        //   mainRun?.runSettings?.runAttemptId ||
        //   lastSuccessfulRun?.runSettings?.runAttemptId ||
        //   "no-run";

        return (
          <ApprovedCheckboxWithRunComparison
            key={`${rowKey}-${mainRun?.runSettings?.runAttemptId || lastSuccessfulRun?.runSettings?.runAttemptId || "no-run"}`}
            rowKey={rowKey}
            rowData={row}
            syncVersion={syncVersion}
            onApprovedToggle={onApprovedToggle!}
            runAttemptId={
              mainRun?.runSettings?.runAttemptId ||
              lastSuccessfulRun?.runSettings?.runAttemptId ||
              "no-run"
            }
          />
        );
      },
    });
  }

  const columnsWithExpandAndDetails: MUIFlatTableColumn[] = [
    ...expandAndCheckboxColumns,
    ...cleanedBaseTableData.columns,
    {
      key: "__details",
      label: " ",
      align: "left",
      sortable: false,
      onSort: undefined,
      renderCell: (row: any) => {
        if (row.type !== "detail") return null;
        const parent = row.__parentRow || {};
        const sku = String(parent.sku ?? "");
        const location = String(parent.location ?? "");
        const supplier = String(parent.supplier ?? "");
        const cacheKey = `${sku}|${location}|${supplier}|${mainRun?.runSettings?.runAttemptId || lastSuccessfulRun?.runSettings?.runAttemptId || "no-run"}`;

        // Derive parent edit information from committed edits in persistent storage
        // Fallback to any needs_review_po fields on the row if present
        const parentEdit = (() => {
          try {
            // Prefer lookup via committed edit storage keyed by PO ID + SKU + Location
            const poId = parent.purchase_order_id || parent.purchaseOrderId;
            if (poId != null && sku && location) {
              const rowKey = `${poId}-${sku}-${location}-${supplier}-${mainRun?.runSettings?.runAttemptId || lastSuccessfulRun?.runSettings?.runAttemptId || "no-run"}`;
              const stored = supplyGuard?.getEdit?.(rowKey);
              const committed = stored?.committedValues || {};
              const q = committed.quantity ?? committed["quantity"];
              const tc = committed.total_cost ?? committed["total_cost"];
              const uc = committed.unit_cost ?? committed["unit_cost"];
              const opd =
                committed.order_placed_date ?? committed["order_placed_date"];
              const dd =
                committed.order_delivery_date ??
                committed["order_delivery_date"];

              // Try to get initial values from parent row to merge with committed values
              const initialQty = parent.quantity
                ? Number(String(parent.quantity).replace(/,/g, ""))
                : null;
              const initialTotalCost = parent.total_cost
                ? Number(String(parent.total_cost).replace(/,/g, ""))
                : null;
              const initialUnitCost = parent.unit_cost
                ? Number(String(parent.unit_cost).replace(/,/g, ""))
                : null;
              const initialOpd = parent.order_placed_date
                ? String(parent.order_placed_date)
                : null;
              const initialDd = parent.order_delivery_date
                ? String(parent.order_delivery_date)
                : null;

              // Merge committed values with initial values to ensure we have all required fields
              // This handles cases where only some fields were edited (e.g., only quantity)
              if (initialQty != null && initialOpd) {
                return {
                  quantity:
                    q != null && q !== ""
                      ? Number(String(q).replace(/,/g, ""))
                      : initialQty,
                  total_cost:
                    tc != null && tc !== ""
                      ? Number(String(tc).replace(/,/g, ""))
                      : initialTotalCost,
                  unit_cost:
                    uc != null && uc !== ""
                      ? Number(String(uc).replace(/,/g, ""))
                      : initialUnitCost,
                  orderPlacedDate:
                    opd != null && opd !== "" ? String(opd) : initialOpd,
                  deliveryDate:
                    dd != null && dd !== "" ? String(dd) : initialDd,
                };
              }

              // Fallback: if we have committed values with required fields, use them
              if (q != null && opd) {
                return {
                  quantity: Number(String(q).replace(/,/g, "")),
                  total_cost:
                    tc != null
                      ? Number(String(tc).replace(/,/g, ""))
                      : undefined,
                  unit_cost:
                    uc != null
                      ? Number(String(uc).replace(/,/g, ""))
                      : undefined,
                  orderPlacedDate: String(opd),
                  deliveryDate: dd != null ? String(dd) : "",
                };
              }
            }

            // Fallback: some rows may not include PO ID in the view; search persisted edits by SKU+Location suffix
            // Note: This fallback is disabled since context doesn't expose iteration methods
            // const suffix = `-${sku}-${location}`;
            // for (const [k, v] of persistentEditStorage.entries()) {
            //   if (k.endsWith(suffix)) {
            //     const committed = v?.committedValues || {};
            //     const q = committed.quantity ?? committed["quantity"];
            //     const opd =
            //       committed.order_placed_date ?? committed["order_placed_date"];
            //     const dd =
            //       committed.order_delivery_date ??
            //       committed["order_delivery_date"];
            //     if (q != null && opd) {
            //       return {
            //         quantity: Number(String(q).replace(/,/g, "")),
            //         orderPlacedDate: String(opd),
            //         deliveryDate: dd != null ? String(dd) : "",
            //       };
            //     }
            //   }
            // }
          } catch (_) {}

          // Fallback: read potential needs_review_po structure on the row
          const needsReviewPO = parent.needs_review_po || parent.needsReviewPO;
          if (
            needsReviewPO &&
            needsReviewPO.quantity &&
            needsReviewPO.order_placed_date &&
            needsReviewPO.delivery_date
          ) {
            return {
              quantity: Number(needsReviewPO.quantity),
              total_cost:
                needsReviewPO.total_cost != null
                  ? Number(needsReviewPO.total_cost)
                  : needsReviewPO.cost != null
                    ? Number(needsReviewPO.cost)
                    : undefined,
              unit_cost:
                needsReviewPO.unit_cost != null
                  ? Number(needsReviewPO.unit_cost)
                  : undefined,
              orderPlacedDate: String(needsReviewPO.order_placed_date),
              deliveryDate: String(needsReviewPO.delivery_date),
            };
          }
          return null;
        })();

        // If no parentEdit, try to construct from parent row data
        const finalParentEdit =
          parentEdit ||
          (() => {
            if (
              parent.quantity &&
              parent.order_placed_date &&
              parent.order_delivery_date
            ) {
              return {
                quantity: Number(String(parent.quantity).replace(/,/g, "")),
                total_cost:
                  parent.total_cost != null
                    ? Number(String(parent.total_cost).replace(/,/g, ""))
                    : undefined,
                unit_cost:
                  parent.unit_cost != null
                    ? Number(String(parent.unit_cost).replace(/,/g, ""))
                    : undefined,
                orderPlacedDate: String(parent.order_placed_date),
                deliveryDate: String(parent.order_delivery_date),
                approved: parent.approved ?? false,
              };
            }
            return null;
          })();

        // Construct initialParentEdit with ORIGINAL values (not edited values)
        // This is used as a fallback in the recalculation logic in nested_details
        const initialParentEditForComponent = (() => {
          const poId = parent.purchase_order_id || parent.purchaseOrderId;
          if (poId != null && sku && location) {
            const rowKey = `${poId}-${sku}-${location}-${supplier}-${mainRun?.runSettings?.runAttemptId || lastSuccessfulRun?.runSettings?.runAttemptId || "no-run"}`;
            const stored = supplyGuard?.getEdit?.(rowKey);

            // If we have stored original values, use those (these are the true original values)
            if (stored?.originalValues) {
              const orig = stored.originalValues;
              const origQty = orig.quantity ?? orig["quantity"];
              const origTotalCost = orig.total_cost ?? orig["total_cost"];
              const origUnitCost = orig.unit_cost ?? orig["unit_cost"];
              const origOpd =
                orig.order_placed_date ?? orig["order_placed_date"];
              const origDd =
                orig.order_delivery_date ?? orig["order_delivery_date"];

              if (origQty != null && origOpd) {
                return {
                  quantity: Number(String(origQty).replace(/,/g, "")),
                  total_cost:
                    origTotalCost != null && origTotalCost !== ""
                      ? Number(String(origTotalCost).replace(/,/g, ""))
                      : undefined,
                  unit_cost:
                    origUnitCost != null && origUnitCost !== ""
                      ? Number(String(origUnitCost).replace(/,/g, ""))
                      : undefined,
                  orderPlacedDate: String(origOpd),
                  deliveryDate:
                    origDd != null && origDd !== "" ? String(origDd) : "",
                  approved: stored.approved ?? false,
                };
              }
            }
          }

          // Fallback: if no stored original values, use parent row data
          // This assumes parent row has original values (which it should if no edits were applied to it)
          if (
            parent.quantity &&
            parent.order_placed_date &&
            parent.order_delivery_date
          ) {
            return {
              quantity: Number(String(parent.quantity).replace(/,/g, "")),
              total_cost:
                parent.total_cost != null
                  ? Number(String(parent.total_cost).replace(/,/g, ""))
                  : undefined,
              unit_cost:
                parent.unit_cost != null
                  ? Number(String(parent.unit_cost).replace(/,/g, ""))
                  : undefined,
              orderPlacedDate: String(parent.order_placed_date),
              deliveryDate: String(parent.order_delivery_date),
              approved: parent.approved ?? false,
            };
          }

          return null;
        })();

        return (
          <NestedDetailsComponent
            key={`${cacheKey}::q=${finalParentEdit?.quantity ?? ""}::tc=${finalParentEdit?.total_cost ?? ""}::uc=${finalParentEdit?.unit_cost ?? ""}::opd=${finalParentEdit?.orderPlacedDate ?? ""}::eta=${finalParentEdit?.deliveryDate ?? ""}`}
            cacheRef={childTablesRef}
            cacheKey={cacheKey}
            sku={sku}
            location={location}
            supplier={supplier}
            parentEdit={initialParentEditForComponent}
            poId={String(
              parent.purchase_order_id ?? parent.purchaseOrderId ?? ""
            )}
          />
        );
      },
    },
  ].map((column) => {
    // For sortable columns, don't override the label - let getFormattedHeader handle it
    // This ensures sort arrows are displayed correctly
    if (column.sortable && column.onSort) {
      return column;
    }
    // For non-sortable columns (expand, details, etc.), set the label explicitly
    return {
      ...column,
      label: column.isExpandColumn
        ? ""
        : column.key === "__edit"
          ? ""
          : columnHeaderMap?.[column.key] || column.label || column.key,
    };
  });

  // Map a virtual index to either a group row (parent) or its single detail row when expanded
  const getRow = (rowIndex: number) => {
    let running = 0;
    for (let i = 0; i < cleanedBaseTableData.rowCount; i++) {
      const parent = cleanedBaseTableData.getRow(i) as any;
      const groupKey = String(i);
      const isExpanded =
        expandedGroups?.has("*") || expandedGroups?.has(groupKey) || false;
      const size = 1 + (isExpanded ? 1 : 0);
      if (rowIndex < running + size) {
        if (rowIndex === running) {
          const result = {
            ...parent,
            type: "group" as const,
            groupKey,
            isExpanded,
            groupSize: 2, // >1 to show expand button
          };
          return result;
        } else {
          return {
            type: "detail" as const,
            groupKey,
            __parentRow: parent,
          };
        }
      }
      running += size;
    }
    return {} as any;
  };

  let rowCount = 0;
  for (let i = 0; i < cleanedBaseTableData.rowCount; i++) {
    const gk = String(i);
    const isExpanded =
      expandedGroups?.has("*") || expandedGroups?.has(gk) || false;
    rowCount += 1 + (isExpanded ? 1 : 0);
  }

  const result = {
    columns: columnsWithExpandAndDetails,
    getRow,
    rowCount,
    columnTotals: cleanedBaseTableData.columnTotals,
    // Add sortConfig to result to ensure table re-renders when sort changes
    _sortKey: sortConfig ? JSON.stringify(sortConfig) : "",
    computeItemKey,
  };

  // LEVEL 3 CACHE REMOVED FOR TESTING
  // matrixMemoRef.current = { key, result };
  return result;
}
